import React, { useState, useCallback, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Heading1,
  Code,
  Undo,
  Redo,
  FileCode2,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Eraser,
  Image as ImageIcon,
} from "lucide-react";

const EditorToolbar = ({
  editor,
  showCode,
  onToggleCode,
  isCodeActive = true,
}) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [imageName, setImageName] = useState("");
  const imageInputRef = useRef(null);

  if (!editor) return null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const setLink = useCallback(() => {
    let url = linkUrl.trim();

    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
      }

      const { empty } = editor.state.selection;
      const chain = editor.chain().focus();

      if (empty) {
        chain
          .insertContent({
            type: "text",
            text: url,
            marks: [{ type: "link", attrs: { href: url } }],
          })
          .run();
      } else {
        chain.extendMarkRange("link").setLink({ href: url }).run();
      }
    }
    setLinkUrl("");
    setShowLinkModal(false);
  }, [editor, linkUrl]);

  const handleOpenLinkModal = () => {
    const currentUrl = editor.getAttributes("link")?.href || "";
    setLinkUrl(currentUrl);
    setShowLinkModal(true);
  };

  const handleCloseLinkModal = () => {
    setShowLinkModal(false);
    setLinkUrl("");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result;
      if (base64) {
        editor.chain().focus().setImage({ src: base64 }).run();
        setShowImageModal(false);
        setImageName("");
      }
    };
    reader.readAsDataURL(file);
  };

  const ToolbarButton = ({ icon, action, active, disabled }) => (
    <Button
      variant={active ? "primary" : "outline-secondary"}
      size="sm"
      className="me-1 mb-1 d-flex align-items-center justify-content-center"
      disabled={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
        action();
      }}
    >
      {icon}
    </Button>
  );

  const handleClearFormatting = () => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .unsetAllMarks()
      .unsetLink()
      .clearNodes()
      .setParagraph()
      .setTextAlign("left")
      .run();
  };

  return (
    <div className="p-2 border-bottom bg-white d-flex flex-wrap align-items-center">
      {isCodeActive && (
        <ToolbarButton
          icon={<FileCode2 size={16} />}
          action={onToggleCode}
          active={showCode}
        />
      )}

      <ToolbarButton
        icon={<Undo size={16} />}
        action={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      />
      <ToolbarButton
        icon={<Redo size={16} />}
        action={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      />

      <ToolbarButton
        icon={<Heading1 size={16} />}
        action={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editor.isActive("heading", { level: 1 })}
      />
      <ToolbarButton
        icon={<Bold size={16} />}
        action={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
      />
      <ToolbarButton
        icon={<Italic size={16} />}
        action={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
      />
      <ToolbarButton
        icon={<Underline size={16} />}
        action={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive("underline")}
      />
      <ToolbarButton
        icon={<Strikethrough size={16} />}
        action={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive("strike")}
      />
      <ToolbarButton
        icon={<Code size={16} />}
        action={() => editor.chain().focus().toggleCode().run()}
        active={editor.isActive("code")}
      />

      <ToolbarButton
        icon={<List size={16} />}
        action={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
      />
      <ToolbarButton
        icon={<ListOrdered size={16} />}
        action={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
      />

      <ToolbarButton
        icon={<LinkIcon size={16} />}
        action={handleOpenLinkModal}
        active={editor.isActive("link")}
      />
      <ToolbarButton
        icon={<ImageIcon size={16} />}
        action={() => setShowImageModal(true)}
      />

      <ToolbarButton
        icon={<AlignLeft size={16} />}
        action={() => editor.chain().focus().setTextAlign("left").run()}
      />
      <ToolbarButton
        icon={<AlignCenter size={16} />}
        action={() => editor.chain().focus().setTextAlign("center").run()}
      />
      <ToolbarButton
        icon={<AlignRight size={16} />}
        action={() => editor.chain().focus().setTextAlign("right").run()}
      />
      <ToolbarButton
        icon={<AlignJustify size={16} />}
        action={() => editor.chain().focus().setTextAlign("justify").run()}
      />

      <ToolbarButton
        icon={<Eraser size={16} />}
        action={handleClearFormatting}
      />

      <Modal show={showLinkModal} onHide={handleCloseLinkModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Insert Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setLink();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <div className="offer-save d-flex m-1">
            <button
              onClick={handleCloseLinkModal}
              className="onboarding-pop-up-btn onboarding-pop-up-cancel mx-2"
            >
              Cancel
            </button>
            <button
              onClick={setLink}
              className={`onboarding-pop-up-btn onboarding-pop-up-submit ms-1 ${
                !linkUrl.trim() ? "btn-disabled" : ""
              }`}
              disabled={!linkUrl.trim()}
            >
              Insert
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Image</Form.Label>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={(e) => {
                setImageName(e.target.files?.[0]?.name || "");
                handleImageUpload(e);
              }}
            />
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => imageInputRef.current?.click()}
              >
                Upload Image
              </Button>
              <span className="text-muted small text-truncate">
                {imageName || "No file chosen"}
              </span>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditorToolbar;
